import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import qs from 'qs';
import {
  IconButtonProps,
  useSnackbar,
  useSnackbarDispatchContext,
  DropdownMenu,
  DropdownMenuTrigger,
  IconButton,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@c2fo/liquidity';
import { EllipsisVerticalIcon } from '@c2fo/liquidity/icons';
import {
  useGlobalPriceDiscoveryOffer,
  useGlobalFixedRateOffer,
  useGlobalVariableRateOffer,
  GlobalOfferMarketPricingType,
} from '@/enterprise/data/useGlobalOffer';
import { BuyerGroupWithStats } from '@/enterprise/data/useGroupByBuyer';
import { useGlobalOfferContext } from '@/enterprise/features/globalOffers/GlobalOfferContext';
import { useGlobalParticipationContext } from '@/enterprise/features/globalOffers/GlobalParticipationContext';
import { trackParticipationMarketEvents } from '@/enterprise/features/globalOffers/utils';
import { useRecurringRulesForm } from '@/enterprise/features/recurringRules/RecurringRulesFormContext';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';
import useExchangeRates from '@/shared/data/useExchangeRates';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useRestrictions from '@/utils/useRestrictions';
import { useIncludeEligibleInvoicesContext } from './IncludeEligibleInvoicesContext';

interface BuyerControlsDropdownProps {
  buyer: BuyerGroupWithStats;
  triggerVariant: Extract<IconButtonProps['variant'], 'ancillary' | 'tertiary'>;
}

const BuyerControlsDropdown = ({ buyer, triggerVariant }: BuyerControlsDropdownProps) => {
  const { t } = useTranslation();
  const { addError } = useDataDog();
  const showSnackbar = useSnackbar();
  const { getRestrictions } = useRestrictions();
  const dispatch = useSnackbarDispatchContext();
  const { openModal: openParticipationModal } = useGlobalParticipationContext();
  const { openModal: openGlobalOfferModal } = useGlobalOfferContext();
  const { openModal: openRecurringRulesForm } = useRecurringRulesForm();
  const { openModal: openIncludeEligibleInvoices } = useIncludeEligibleInvoicesContext();
  const { trackEnterpriseEvent } = useReporting();
  const { data: exchangeRates } = useExchangeRates();
  const { BENCHMARK, FIXED, NAME_YOUR_RATE } = buyer.takerMarketGroups;
  const takerMarketsToEdit = [...BENCHMARK.takerMarkets, ...FIXED.takerMarkets, ...NAME_YOUR_RATE.takerMarkets];
  const { canEditInvoices, canEditOffers, canEditRecurringRules } = getRestrictions(takerMarketsToEdit);
  const { mutateAsync: submitGlobalPriceDiscoveryParticipation } = useGlobalPriceDiscoveryOffer(
    NAME_YOUR_RATE.takerMarkets
  );
  const { mutateAsync: submitGlobalFixedRateParticipation } = useGlobalFixedRateOffer(FIXED.takerMarkets);
  const { mutateAsync: submitGlobalVariableRateParticipation } = useGlobalVariableRateOffer(BENCHMARK.takerMarkets);

  // Display the include eligible invoices button if the following conditions are met:
  // 1. The user has the ability to edit invoices
  // 2. There are eligible taker markets with excluded invoices
  const displayIncludeEligibleInvoicesButton =
    canEditInvoices &&
    (BENCHMARK.rollup.excludedAmount > 0 ||
      FIXED.rollup.excludedAmount > 0 ||
      NAME_YOUR_RATE.rollup.excludedAmount > 0);

  const onParticipationSubmit = async ({ isEnabled }: { isEnabled: boolean }) => {
    if (!canEditOffers) {
      return;
    }

    // if turning participation on, send to modal
    if (isEnabled) {
      openParticipationModal(takerMarketsToEdit);
      return;
    }

    const submissionBatchId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const messageId = buyer.makerOrganizationUuid;
    const succeededGroups: { type: GlobalOfferMarketPricingType; groupTakerMarkets: typeof takerMarketsToEdit }[] = [];

    const onSuccess = (type: GlobalOfferMarketPricingType, groupTakerMarkets: typeof takerMarketsToEdit) => {
      succeededGroups.push({ type, groupTakerMarkets });
      trackParticipationMarketEvents({
        takerMarkets: groupTakerMarkets,
        pricingType: type,
        isEnabled,
        exchangeRates,
        submissionBatchId,
        trackEnterpriseEvent,
      });
    };

    dispatch?.({
      type: 'enqueue',
      message: {
        id: messageId,
        closeable: false,
        content: t('buyerControls.turningParticipationOff'),
        loading: true,
        open: true,
      },
    });

    try {
      const promises = [];

      if (NAME_YOUR_RATE.takerMarkets.length > 0) {
        promises.push(
          submitGlobalPriceDiscoveryParticipation(
            { isEnabled },
            { onSuccess: () => onSuccess('NAME_YOUR_RATE', NAME_YOUR_RATE.takerMarkets) }
          )
        );
      }

      if (FIXED.takerMarkets.length > 0) {
        promises.push(
          submitGlobalFixedRateParticipation({ isEnabled }, { onSuccess: () => onSuccess('FIXED', FIXED.takerMarkets) })
        );
      }

      if (BENCHMARK.takerMarkets.length > 0) {
        promises.push(
          submitGlobalVariableRateParticipation(
            { isEnabled },
            { onSuccess: () => onSuccess('BENCHMARK', BENCHMARK.takerMarkets) }
          )
        );
      }

      await Promise.all(promises);

      if (succeededGroups.length > 0) {
        trackEnterpriseEvent('global-participation::submitted', {
          isEnabled,
          submissionBatchId,
          NAME_YOUR_RATE: succeededGroups.find((g) => g.type === 'NAME_YOUR_RATE')?.groupTakerMarkets.length ?? 0,
          BENCHMARK: succeededGroups.find((g) => g.type === 'BENCHMARK')?.groupTakerMarkets.length ?? 0,
          FIXED: succeededGroups.find((g) => g.type === 'FIXED')?.groupTakerMarkets.length ?? 0,
        });
      }

      dispatch?.({ type: 'dequeue', id: messageId });
      showSnackbar({
        message: t('buyerControls.participationTurnedOff'),
      });
    } catch (error) {
      dispatch?.({ type: 'dequeue', id: messageId });
      showSnackbar({
        message: t('buyerControls.participationUpdateError'),
      });
      addError('Enterprise Buyer Participation Off Error', {
        error,
        location: 'BuyerCards -> BuyerDropdown -> onParticipationSubmit',
        takerMarkets: takerMarketsToEdit.map((tm) => ({
          makerOrganizationUuid: tm.makerOrganizationUuid,
          marketUuid: tm.marketUuid,
          supplierDivisionUuid: tm.takerDivisionId,
        })),
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton
          icon={EllipsisVerticalIcon}
          name={t('buyerControls.customerActions')}
          size="sm"
          variant={triggerVariant}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" avoidCollisions={false} collisionPadding={24}>
        <DropdownMenuItem
          {...(canEditOffers
            ? {
                onClick: () => {
                  openGlobalOfferModal(
                    buyer.takerMarketGroups.NAME_YOUR_RATE.takerMarkets,
                    buyer.makerOrganizationName
                  );
                  trackEnterpriseEvent('global-offer::clicked');
                },
                disabled: buyer.takerMarketGroups.NAME_YOUR_RATE.takerMarkets.length === 0,
              }
            : { disabled: true })}
        >
          {t('globalActions.modifyRate')}
        </DropdownMenuItem>
        <DropdownMenuItem
          {...(canEditRecurringRules
            ? { onClick: () => openRecurringRulesForm(buyer.takerMarkets) }
            : { disabled: true })}
        >
          {t('automation.modifyInvoiceRules')}
        </DropdownMenuItem>
        <DropdownMenuItem
          {...(displayIncludeEligibleInvoicesButton
            ? {
                onClick: () => {
                  trackEnterpriseEvent('invoices::include-excluded::clicked');
                  openIncludeEligibleInvoices(buyer.takerMarkets);
                },
              }
            : { disabled: true })}
        >
          {t('globalActions.includeExcludedInvoices')}
        </DropdownMenuItem>
        <DropdownMenuItem
          {...(canEditOffers
            ? {
                onClick: () => {
                  onParticipationSubmit({ isEnabled: true });
                  trackEnterpriseEvent('global-participation::clicked');
                },
                disabled: takerMarketsToEdit.length === 0,
              }
            : { disabled: true })}
        >
          {t('globalActions.turnParticipationOn')}
        </DropdownMenuItem>
        <DropdownMenuItem
          {...(canEditOffers
            ? {
                onClick: () => {
                  onParticipationSubmit({ isEnabled: false });
                },
                disabled: takerMarketsToEdit.length === 0,
              }
            : { disabled: true })}
        >
          {t('globalActions.turnParticipationOff')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            to={asEnterpriseLink(
              `history?${qs.stringify({
                makerIds: [buyer.makerId],
                includeFuturePayDates: true,
              })}`
            )}
          >
            {t('core.viewHistory')}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default BuyerControlsDropdown;
